/**
 * SEO Utilities
 *
 * Spec Requirements:
 * - Lines 103-104: SEO compatible, mobile friendly
 * - Meta tags, schema.org, sitemaps
 * - Backlinks and tag words for products
 */

export interface SEOMetaData {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  robots?: string;
}

export interface ProductSchema {
  name: string;
  description: string;
  image: string[];
  sku: string;
  price: number;
  priceCurrency: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
  brand?: string;
  category?: string;
  rating?: {
    ratingValue: number;
    reviewCount: number;
  };
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

/**
 * Set meta tags for SEO
 */
export const setMetaTags = (meta: SEOMetaData): void => {
  // Title
  if (meta.title) {
    document.title = meta.title;
    updateMetaTag('og:title', meta.ogTitle || meta.title);
    updateMetaTag('twitter:title', meta.twitterTitle || meta.title);
  }

  // Description
  if (meta.description) {
    updateMetaTag('description', meta.description);
    updateMetaTag('og:description', meta.ogDescription || meta.description);
    updateMetaTag('twitter:description', meta.twitterDescription || meta.description);
  }

  // Keywords
  if (meta.keywords && meta.keywords.length > 0) {
    updateMetaTag('keywords', meta.keywords.join(', '));
  }

  // Open Graph
  if (meta.ogImage) updateMetaTag('og:image', meta.ogImage);
  updateMetaTag('og:type', 'website');

  // Twitter
  updateMetaTag('twitter:card', meta.twitterCard || 'summary_large_image');
  if (meta.twitterImage) updateMetaTag('twitter:image', meta.twitterImage);

  // UK locale signal (always en-GB for this market)
  updateMetaTag('og:locale', 'en_GB');

  // Canonical + self-referencing hreflang + og:url.
  // Defaults to the production URL for the current path so prerendered pages get a
  // correct self-canonical (never the build host) even if a page omits one.
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const canonical = meta.canonical || `https://www.siluxmobile.co.uk${path}`;
  updateLinkTag('canonical', canonical);
  updateHreflangTag('en-GB', canonical);
  updateHreflangTag('x-default', canonical);
  const ogUrl = meta.ogUrl && !/localhost|127\.0\.0\.1/.test(meta.ogUrl) ? meta.ogUrl : canonical;
  updateMetaTag('og:url', ogUrl);

  // Robots
  if (meta.robots) {
    updateMetaTag('robots', meta.robots);
  }
};

/**
 * Update or create a meta tag
 */
const updateMetaTag = (name: string, content: string): void => {
  let element = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;

  if (!element) {
    element = document.querySelector(`meta[property="${name}"]`) as HTMLMetaElement;
  }

  if (!element) {
    element = document.createElement('meta');

    if (name.startsWith('og:') || name.startsWith('twitter:')) {
      element.setAttribute('property', name);
    } else {
      element.setAttribute('name', name);
    }

    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

/**
 * Update or create a link tag
 */
const updateLinkTag = (rel: string, href: string): void => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

/**
 * Update or create a hreflang alternate link (keyed by hreflang value)
 */
const updateHreflangTag = (hreflang: string, href: string): void => {
  let element = document.querySelector(
    `link[rel="alternate"][hreflang="${hreflang}"]`
  ) as HTMLLinkElement;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'alternate');
    element.setAttribute('hreflang', hreflang);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

/**
 * Generate Product Schema (JSON-LD)
 */
export const generateProductSchema = (product: ProductSchema): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.image,
    sku: product.sku,
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: product.priceCurrency,
      availability: `https://schema.org/${product.availability || 'InStock'}`
    }
  };

  if (product.brand) {
    (schema as any).brand = {
      '@type': 'Brand',
      name: product.brand
    };
  }

  if (product.category) {
    (schema as any).category = product.category;
  }

  if (product.rating) {
    (schema as any).aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: product.rating.ratingValue,
      reviewCount: product.rating.reviewCount
    };
  }

  return JSON.stringify(schema);
};

/**
 * Generate Breadcrumb Schema (JSON-LD)
 */
export const generateBreadcrumbSchema = (items: BreadcrumbItem[]): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return JSON.stringify(schema);
};

/**
 * Generate FAQ Schema (JSON-LD)
 */
export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return JSON.stringify(schema);
};

/**
 * Generate Organization Schema (JSON-LD)
 */
export const generateOrganizationSchema = (): string => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Silux Mobile',
    url: 'https://www.siluxmobile.co.uk',
    logo: 'https://www.siluxmobile.co.uk/logo.png',
    telephone: '+44 1246 912899',
    email: 'info@siluxmobile.co.uk',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '13 Prospect House, Colliery Close, Staveley',
      addressLocality: 'Chesterfield',
      addressRegion: 'Derbyshire',
      postalCode: 'S43 3QE',
      addressCountry: 'GB'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+44 1246 912899',
      email: 'info@siluxmobile.co.uk',
      contactType: 'customer service',
      areaServed: 'GB',
      availableLanguage: 'en-GB',
      // Office hours Mon–Fri 9am–5pm, closed weekends and bank holidays. Chat support is 24 hours.
      hoursAvailable: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00'
      }
    },
    sameAs: [
      'https://www.facebook.com/siluxmobile',
      'https://www.twitter.com/siluxmobile'
    ]
  };

  return JSON.stringify(schema);
};

/**
 * Inject JSON-LD schema into page
 */
export const injectSchema = (schemaJson: string, id: string = 'schema-org'): void => {
  // Remove existing schema with same ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  // Create and inject new schema
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.text = schemaJson;
  document.head.appendChild(script);
};

/**
 * Generate sitemap entry
 */
export interface SitemapEntry {
  url: string;
  lastmod?: string;
  changefreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  priority?: number;
}

/**
 * Default SEO configuration for Silux Mobile
 */
export const defaultSEO: SEOMetaData = {
  title: 'Silux Mobile - Cheap SIM Only Deals & Mobile Broadband UK',
  description: 'Cheap SIM only deals, data SIM cards and 4G/5G mobile broadband on the Three, O2 and Vodafone networks. No-contract rolling SIMs, no credit check, no price rises — free Royal Mail Tracked 48 delivery across the UK.',
  keywords: [
    'cheap SIM only deals UK', 'SIM only deals', 'data SIM cards', 'data only SIM',
    'mobile broadband UK', '4G broadband', '5G SIM only', 'no contract SIM',
    'free SIM card', 'Three network SIM', 'unlimited data SIM', 'MiFi broadband',
    'home broadband no landline', 'SIM only provider', 'mobile network provider UK',
    'cheap mobile data', 'pay monthly SIM no credit check'
  ],
  ogImage: '/og-image.png',
  twitterCard: 'summary_large_image',
  robots: 'index, follow'
};
